import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, ChevronRight, Loader2, Network, Search } from "lucide-react";

import { api } from "@/lib/api";
import { Header } from "@/components/header";
import { Badge, Empty, ErrorView, Loading, SearchInput } from "@/components/kit";
import { C, date, eur } from "@/lib/format";

export function useDebounced(value, ms = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function Group({ g }) {
  return (
    <div data-testid={`referral-group-${g.parrain.id}`} className="flex flex-col gap-2 rounded-[20px] border border-[#333333] bg-[#1E1E1E] p-4">
      <Link to={`/admin/agent/${g.parrain.id}`} data-testid={`referral-parrain-${g.parrain.id}`} className="focus-ring -m-1 flex items-center gap-3 rounded-xl p-1 hover:bg-white/5">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#292415]"><Award className="h-5 w-5 text-[#D4AF37]" aria-hidden /></span>
        <span className="min-w-0 flex-1">
          <span className="t-h3 block truncate">{g.parrain.name}</span>
          <span className="t-caption block truncate">Code {g.parrain.referral_code} · {g.parrain.agency}</span>
        </span>
        <span className="t-h2 !text-[#D4AF37]" data-testid={`referral-count-${g.parrain.id}`}>{g.count}</span>
      </Link>
      <p className="t-caption">Récompenses : {eur(g.bonus.pending)} à valider · {eur(g.bonus.validated)} à payer · {eur(g.bonus.paid)} payé</p>
      {g.filleuls.map((f) => (
        <Link key={f.id} to={`/admin/agent/${f.id}`} data-testid={`referral-filleul-${f.id}`} className="focus-ring flex items-center gap-2 border-l-2 border-[#D4AF37] py-2 pl-3 transition-colors hover:bg-white/5">
          <ArrowRight className="h-3.5 w-3.5 text-[#9E9E9E]" aria-hidden />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="truncate text-sm font-medium text-white">{f.name}</span>
            <span className="t-caption">{f.agency ? `${f.agency} · ` : ""}inscrit le {date(f.created_at)} · {f.signed} contrat(s)</span>
            <span className="flex flex-wrap gap-1">
              {f.status === "deleted" && <Badge label="Compte supprimé" color={C.silver} />}
              {!f.email_verified && f.status !== "deleted" && <Badge label="E-mail non confirmé" color={C.silver} />}
              {f.suspicious && <Badge label="À vérifier (même IP)" color={C.warning} />}
            </span>
          </span>
          <ChevronRight className="h-4 w-4 text-[#9E9E9E]" aria-hidden />
        </Link>
      ))}
    </div>
  );
}

export default function AdminReferrals() {
  const [search, setSearch] = useState("");
  const term = useDebounced(search.trim());
  const q = useQuery({
    queryKey: ["admin-referrals", term],
    queryFn: () => api(`/admin/referrals?q=${encodeURIComponent(term)}`),
    placeholderData: (prev) => prev,
  });
  return (
    <div data-testid="admin-referrals-screen" className="flex flex-col">
      <Header title="Parrainages" subtitle="Qui a parrainé qui" />
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-3 p-4 pb-8 md:p-8">
        <div className="max-w-xl">
          <SearchInput icon={Search} testID="admin-referrals-search-input" value={search} onChange={setSearch} placeholder="Parrain, filleul, code, agence…" />
        </div>
        {q.isLoading ? <Loading /> : q.isError || !q.data ? <ErrorView message="Erreur de chargement." onRetry={() => q.refetch()} /> : (
          <>
            <div className="flex items-center gap-2">
              <p className="t-caption" data-testid="admin-referrals-count">{q.data.parrains} parrain(s) · {q.data.filleuls} filleul(s)</p>
              {q.isFetching && q.isPlaceholderData ? <Loader2 data-testid="admin-referrals-searching" className="h-4 w-4 animate-spin text-[#D4AF37]" /> : null}
            </div>
            {q.data.items.length ? (
              <div className="grid items-start gap-3 lg:grid-cols-2">{q.data.items.map((g) => <Group key={g.parrain.id} g={g} />)}</div>
            ) : term ? (
              <Empty icon={Search} title="Aucun résultat" text={`Aucun parrain ni filleul ne correspond à « ${term} ».`} />
            ) : (
              <Empty icon={Network} title="Aucun parrainage" text="Dès qu'un agent s'inscrit avec le code d'un collègue, le lien parrain → filleul apparaît ici." />
            )}
          </>
        )}
      </div>
    </div>
  );
}
